import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Shield, Users, Calendar, MessageSquare, ArrowLeft, AlertTriangle, CreditCard, FileText } from 'lucide-react';

interface AdminLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ children, title, subtitle }) => {
  const location = useLocation();

  const navigationItems = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: Shield,
      current: location.pathname === '/admin'
    },
    {
      name: 'Events',
      href: '/admin/events',
      icon: Calendar,
      current: location.pathname === '/admin/events'
    },
    {
      name: 'Blog',
      href: '/admin/blog',
      icon: FileText,
      current: location.pathname.startsWith('/admin/blog')
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      current: location.pathname === '/admin/users'
    },
    {
      name: 'Groups',
      href: '/admin/groups',
      icon: MessageSquare,
      current: location.pathname === '/admin/groups'
    },
    {
      name: 'Payments',
      href: '/admin/payments',
      icon: CreditCard,
      current: location.pathname === '/admin/payments'
    },
    {
      name: 'Reports',
      href: '/admin/reports',
      icon: AlertTriangle,
      current: location.pathname === '/admin/reports'
    }
  ];

  return (
    <div className="min-h-screen pb-10">
      {/* Top Navigation */}
      <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
        <div className="sf-nav flex h-14 items-center justify-between px-4 sm:px-5">
          <div className="flex items-center space-x-4">
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-sm font-medium text-fg-nav transition-colors hover:text-fg-bright"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to App</span>
            </Link>
            <div className="h-6 w-px bg-white/[.08]" />
            <div className="flex items-center space-x-2">
              <Shield className="h-5 w-5 text-neon-cyan" />
              <span className="text-lg font-bold tracking-tight text-fg-bright">Admin panel</span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex gap-8">
          {/* Sidebar Navigation */}
          <div className="w-64 flex-shrink-0">
            <nav className="sf-card space-y-1 p-3">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`w-full ${item.current ? 'sf-nav-item-active' : 'sf-nav-item'}`}
                  >
                    <Icon
                      className={`h-4 w-4 flex-shrink-0 ${item.current ? 'text-neon-cyan' : ''}`}
                    />
                    {item.name}
                  </Link>
                );
              })}
            </nav>
          </div>

          {/* Main Content */}
          <div className="flex-1 min-w-0">
            {/* Page Header */}
            {title && (
              <div className="mb-8">
                <div className="sf-eyebrow mb-3">// admin</div>
                <h1 className="text-3xl font-bold tracking-tight text-fg-bright">{title}</h1>
                {subtitle && (
                  <p className="mt-2 text-sm text-fg-muted">{subtitle}</p>
                )}
              </div>
            )}

            {/* Page Content */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
